import { useEffect, useState, type FormEvent } from 'react';
import { Button, Input, Modal, useToast } from '../../components/primitives';
import { api, ApiError } from '../../lib/api';
import { useAuth } from './AuthContext';
import { cx } from '../../lib/cx';
import styles from './DeleteAccountModal.module.css';

type Mode = 'deactivate' | 'delete';

const CONFIRM_WORD = 'SMAZAT';

/**
 * Zrušení vlastního účtu. Uživatel si vybere:
 *  - deaktivace — účet se zablokuje, data zůstanou (obnova sám při přihlášení),
 *  - smazání    — účet i všechna data se nevratně smažou (navíc musí napsat SMAZAT).
 * Obojí vyžaduje heslo. Po úspěchu odhlásí.
 */
export function DeleteAccountModal({
  open,
  onClose,
  initialMode = 'deactivate',
}: {
  open: boolean;
  onClose: () => void;
  initialMode?: Mode;
}) {
  const { logout } = useAuth();
  const toast = useToast();
  const [mode, setMode] = useState<Mode>(initialMode);

  // Menu otevírá okno rovnou s vybranou volbou (Deaktivovat / Smazat).
  useEffect(() => {
    if (open) setMode(initialMode);
  }, [open, initialMode]);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit =
    !!password && (mode === 'deactivate' || confirm.trim().toUpperCase() === CONFIRM_WORD) && !submitting;

  function close() {
    if (submitting) return;
    setPassword('');
    setConfirm('');
    setError(null);
    setMode('deactivate');
    onClose();
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.post<{ message: string }>('/auth/delete-account', { password, mode });
      toast.success(res.message, 6000);
      logout();
    } catch (err) {
      const detail =
        err instanceof ApiError && err.detail && typeof err.detail === 'object' && 'detail' in err.detail
          ? String((err.detail as { detail: unknown }).detail)
          : null;
      setError(detail ?? 'Nelze se spojit se serverem.');
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={mode === 'delete' ? 'Smazat účet' : 'Deaktivovat účet'}
      closeOnBackdrop={!submitting}
      footer={
        <>
          <Button onClick={close} disabled={submitting}>
            Zpět
          </Button>
          <Button type="submit" form="delete-account-form" variant="danger" disabled={!canSubmit}>
            {submitting
              ? 'Provádím…'
              : mode === 'delete'
                ? 'Smazat účet i data'
                : 'Deaktivovat účet'}
          </Button>
        </>
      }
    >
      <form id="delete-account-form" className={styles.form} onSubmit={handleSubmit} noValidate>
        <div className={styles.options} role="radiogroup" aria-label="Způsob zrušení účtu">
          <label className={cx(styles.option, mode === 'deactivate' && styles.optionActive)}>
            <input
              type="radio"
              name="mode"
              value="deactivate"
              checked={mode === 'deactivate'}
              onChange={() => setMode('deactivate')}
            />
            <span>
              <strong>Deaktivovat účet</strong>
              <span className={styles.desc}>
                Účet se uspí, stavby, dokumenty a archiv zůstanou uložené. Kdykoli ho obnovíte sami —
                stačí se znovu přihlásit a potvrdit obnovení.
              </span>
            </span>
          </label>

          <label className={cx(styles.option, mode === 'delete' && styles.optionActive, styles.optionDanger)}>
            <input
              type="radio"
              name="mode"
              value="delete"
              checked={mode === 'delete'}
              onChange={() => setMode('delete')}
            />
            <span>
              <strong>Smazat účet i všechna data</strong>
              <span className={styles.desc}>
                Nevratně se smažou všechny vaše stavby, dokumenty, archiv, adresář firem, podpisy a další
                záznamy. Přijdou o ně i kolegové, se kterými je sdílíte. Obnovit to nepůjde.
              </span>
            </span>
          </label>
        </div>

        <Input
          label="Pro potvrzení zadejte heslo"
          type="password"
          autoComplete="current-password"
          required
          compact
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        {mode === 'delete' && (
          <Input
            label={
              <>
                Napište <b>{CONFIRM_WORD}</b>
              </>
            }
            required
            compact
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="off"
          />
        )}

        {error && (
          <div className={styles.error} role="alert">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
